// Utilidad para exportar eventos a calendario (.ics)

import { formatRangeForHumans } from './planCatalog';

const sanitizeText = (text) => {
  if (!text) return '';
  return text.toString().replace(/\r?\n/g, '\\n').replace(/,/g, '\\,');
};

function formatICSDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T090000`;
}

function generateUID(day, month, year) {
  return `${day}-${month}-${year}-${Date.now()}@numerathum.numerologia-cotidiana.com`;
}

/**
 * Genera un archivo .ics con los días importantes del mes o año
 * @param {Array} importantDays - Array de días importantes con sus datos
 * @param {Date} currentDate - Fecha actual para el contexto del mes
 * @param {Object} userData - Datos del usuario para personalizar
 * @returns {string} - Contenido del archivo .ics
 */
export function generateICSFile(importantDays, currentDate, userData = {}, options = {}) {
  const {
    calendarName = 'Numerathum - Días Importantes',
    calendarDescription = 'Tus días especiales de energía numerológica',
    timezone = 'America/Mexico_City',
    planRange = null
  } = options;

  // Soporta dos formas de entrada:
  // - Mes (legacy): { day: 25, info: {...} } + currentDate (mes/año contexto)
  // - Fecha absoluta (multi-mes / anual): { date: Date | ISO-string, info: {...} }
  const baseYear = currentDate.getFullYear();
  const baseMonth = currentDate.getMonth();

  const resolveEventDate = (dayInfo) => {
    if (dayInfo && dayInfo.date) {
      const raw = dayInfo.date instanceof Date ? dayInfo.date : new Date(dayInfo.date);
      return Number.isNaN(raw.getTime()) ? null : raw;
    }
    if (dayInfo && Number.isFinite(dayInfo.day)) {
      const raw = new Date(baseYear, baseMonth, dayInfo.day);
      return Number.isNaN(raw.getTime()) ? null : raw;
    }
    return null;
  };

  const filteredDays = Array.isArray(importantDays)
    ? importantDays
        .map((dayInfo) => ({ dayInfo, eventDate: resolveEventDate(dayInfo) }))
        .filter(({ eventDate }) => Boolean(eventDate))
        .filter(({ eventDate }) => {
          if (!planRange) return true;
          if (planRange.start && eventDate < planRange.start) return false;
          if (planRange.end && eventDate > planRange.end) return false;
          return true;
        })
        .map(({ dayInfo }) => dayInfo)
    : [];

  if (!filteredDays.length) {
    throw new Error('No hay días importantes dentro del rango permitido por tu plan.');
  }

  const sanitizedName = sanitizeText(calendarName);
  const sanitizedDescription = sanitizeText(calendarDescription);

  // Encabezado del archivo ICS
  let icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//NUMERATHUM//Mi Brújula del Tiempo//ES',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${sanitizedName}`,
    `X-WR-TIMEZONE:${timezone}`,
    `X-WR-CALDESC:${sanitizedDescription}`
  ].join('\r\n');

  // Agregar cada día importante como evento
  filteredDays.forEach(dayInfo => {
    const eventDate = resolveEventDate(dayInfo);
    if (!eventDate) return;

    const year = eventDate.getFullYear();
    const month = eventDate.getMonth() + 1; // 1-12
    const day = eventDate.getDate();
    
    // Acceder correctamente a las propiedades del día
    const info = dayInfo.info || dayInfo;
    const emoji = info.emoji || '';
    const label = info.label || 'Día Especial';
    const desc = info.description || info.explanation || '';
    
    // Título del evento
    const summary = sanitizeText(`${emoji ? emoji + ' ' : ''}${label} - Día ${day}`);
    
    // Descripción del evento
    const description = sanitizeText(desc).substring(0, 500); // Limitar longitud

    // Crear evento
    const event = [
      '',
      'BEGIN:VEVENT',
      `UID:${generateUID(day, month, year)}`,
      `DTSTAMP:${formatICSDate(new Date())}`,
      `DTSTART;VALUE=DATE:${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`,
      `DTEND;VALUE=DATE:${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'TRANSP:TRANSPARENT',
      'BEGIN:VALARM',
      'TRIGGER:-PT2H',
      'ACTION:DISPLAY',
      `DESCRIPTION:Recuerda: ${summary}`,
      'END:VALARM',
      'END:VEVENT'
    ].join('\r\n');

    icsContent += event;
  });

  // Cerrar calendario
  icsContent += '\r\nEND:VCALENDAR';

  return icsContent;
}

/**
 * Descarga el archivo .ics
 * @param {string} icsContent - Contenido del archivo ICS
 * @param {string} filename - Nombre del archivo
 */
export function downloadICS(icsContent, filename = 'numerologia-dias-importantes.ics') {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  
  // Fallback para navegadores legacy (Edge antiguo, IE)
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
    return;
  }
  
  const link = document.createElement('a');
  const url = window.URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  
  // Dar tiempo al navegador para procesar la descarga antes de limpiar
  // Esto soluciona problemas en Edge y otros navegadores donde el click es asíncrono
  setTimeout(() => {
    if (link.parentNode) {
      document.body.removeChild(link);
    }
    window.URL.revokeObjectURL(url);
  }, 1500);
}

/**
 * Función principal para exportar calendario
 * @param {Array} importantDays - Días importantes
 * @param {Date} currentDate - Fecha actual
 * @param {Object} userData - Datos del usuario
 */
export function exportToCalendar(importantDays, currentDate, userData = {}, options = {}) {
  const baseDays = Array.isArray(importantDays) ? importantDays : [];
  if (baseDays.length === 0) {
    alert('No hay días importantes para agregar al calendario.');
    return;
  }

  try {
    let description = options.calendarDescription;
    if (!description && options.planRange) {
      description = `Cobertura: ${formatRangeForHumans(options.planRange.start, options.planRange.end)}`;
    }

    const icsContent = generateICSFile(baseDays, currentDate, userData, {
      ...options,
      calendarDescription: description || 'Tus días especiales de energía numerológica'
    });
    
    // Usar filename personalizado si se proporciona, sino generar uno por defecto
    const filename = options.filename
      ? String(options.filename)
      : (() => {
          const monthName = currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
          return `numerologia-${monthName.replace(/\s+/g, '-').toLowerCase()}.ics`;
        })();
    downloadICS(icsContent, filename);
  } catch (error) {
    console.error('Error al generar archivo de calendario:', error);
    alert(error.message || 'Hubo un error al generar el archivo. Por favor, intenta de nuevo.');
  }
}

